import React from 'react';

import {EntitySummary} from 'constants/UserConstants';
import {Organization} from 'orgs/OrganizationConstants';
import {Party} from 'parties/PartyConstants';
import {FilterKey} from 'search/SearchConstants';
import EntityStore from 'stores/EntityStore';
import {ButtonSize} from 'ui-library/buttons/Button';
import {EntityInputWithSearch, UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import {
  EntitySummariesByHandleFetcher,
  getEntityDisplayName,
} from 'ui-library/entity-input/EntitySummariesByHandleFetcher';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import pick from 'util/pick';

import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './FilterFormConstants';
import FilterFormSkeletonNodeLayout from './FilterFormSkeletonNodeLayout';
import PrimaryFilterFormSkeleton from './PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from './SecondaryFilterFormSkeleton';

interface Props extends BaseFilterFormProps {
  filterStyle: FilterStyle;
  onChangeHandles: (items: Array<string>) => void;
  onEnter?: () => void;
  onRemove?: () => void;
  handles: Array<string>;
  prefix: string;
}

function getDropdownToggleText(
  prefix: string,
  handles: Array<string>,
  entitySummariesByHandle: {[id: string]: EntitySummary}
): string {
  return handles.length
    ? `${prefix}: ${handles
        .map((handle) => getEntityDisplayName(entitySummariesByHandle, handle))
        .join(', ')}`
    : prefix;
}

interface FilterFormSkeletonProps {
  filterStyle: FilterStyle;
  entityInput: JSX.Element;
  buttonSize: ButtonSize;
  handles: Array<string>;
  prefix: string;
  entitySummariesByHandle: {[id: string]: EntitySummary};
  onChangeFilterKey?: (value: FilterKey) => void;
  selectedFilterKey?: FilterKey;
  onRemove?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  skeletonProps: any;
}

function UserFilterFormSkeleton(props: FilterFormSkeletonProps): JSX.Element {
  if (props.filterStyle === FilterStyle.PRIMARY) {
    return (
      <PrimaryFilterFormSkeleton
        {...props.skeletonProps}
        buttonSize={props.buttonSize}
        content={<FilterFormSkeletonNodeLayout nodeGrid={[[props.entityInput]]} />}
        dropdownToggleText={getDropdownToggleText(props.prefix, props.handles, props.entitySummariesByHandle)}
      />
    );
  } else {
    return (
      <SecondaryFilterFormSkeleton
        {...props.skeletonProps}
        nodeGrid={[[props.entityInput]]}
        onChangeFilterKey={props.onChangeFilterKey}
        selectedFilterKey={props.selectedFilterKey}
        onRemove={props.onRemove}
      />
    );
  }
}

export default function UserSearchByLabelFilterForm(props: Props): JSX.Element {
  const skeletonProps = pick(props, ...BASE_FILTER_FORM_PROP_NAMES);
  const currentUser = EntityStore.currentUser();
  const organizations: Array<Organization> = currentUser ? currentUser.organizations : [];
  return (
    <EntitySummariesByHandleFetcher handles={props.handles}>
      {(entitySummariesByHandle) => (
        <UserFilterFormSkeleton
          skeletonProps={skeletonProps}
          filterStyle={props.filterStyle}
          entityInput={
            <EntityInputWithSearch
              size={TagsInputSize.SMALL}
              onChange={(entities: Array<Party>) => {
                props.onChangeHandles(entities.map(({handle}) => handle));
              }}
              onEnter={props.onEnter}
              userSearchType={UserSearchType.All}
              allowBenchlingApps={true}
              allowTeams={false}
              allowOrganizations={false}
              organizationIds={organizations.map(({id}) => id)}
              value={props.handles.map((handle) => ({
                handle,
                // We cannot get the real ID because EntitySummariesByHandleFetcher might not have the entity.
                // So we pass in a fake ID and handle the display name here instead of down in EntityInput.
                id: `handle$\{${handle}}`,
                text: getEntityDisplayName(entitySummariesByHandle, handle),
              }))}
              enableNewEntityInput={true}
              shouldAllowMultiple={true}
              emptyText="No apps or users found"
            />
          }
          buttonSize={props.buttonSize}
          prefix={props.prefix}
          handles={props.handles}
          entitySummariesByHandle={entitySummariesByHandle}
          onChangeFilterKey={props.onChangeFilterKey}
          selectedFilterKey={props.selectedFilterKey}
          onRemove={props.onRemove}
        />
      )}
    </EntitySummariesByHandleFetcher>
  );
}
